// https://github.com/photolane/parabo-next/blob/e52242f29c111d82078a85a1c6dc252fc6e761c0/_templates/product.html
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import queryString from 'utils/queryString';
import productStillImages from 'utils/productStillImages';
import { Alert, Button, Section, Container, Grid, Typography, Card } from 'components/base';
import {
  Accordion,
  CustomerReview,
  HtmlContent,
  ProductCard,
  ProductListCarousel,
  ProductDetailCarousel,
  ScriptMarkupFaq,
  ScriptMarkupProduct,
  faqContentToItems,
  getCloudinaryUrl,
} from 'components/rich';
import useDataContext from 'data/context/data';
import { formatPrice } from 'utils/format';
import useCartContext from 'ecommerce/context/cart';
import ProductConfiguration, { prepareBundleData } from 'ecommerce/components/productConfiguration';
import { getInitialPriceData } from 'ecommerce/lib/productConfiguration';
import useEditorContext from 'editor/context/editorContext';

import { EditorPageLink, CartPageLink, ProductDetailLink } from '../../components/pageLinks';
import { Head, Layout } from '../../components/layout';
import SETTINGS from '../../settings';

import tracker from '../../lib/tracker';
import PexesoGamePreview from 'editor/share/pexesoPreview';
import EditorTheme from '../../specifications/theme/editor';
import { usesProductPageLayout } from '../../lib/productPage.mjs';
import ProductPage from '../../components/product/productPage';

/**
 * Product has many skuProducts. In case of bundle, there is also secondaryProduct.
 * By default is secondaryProductIndex as -1 - that means "Print Only" version
 */
const LegacyProductDetailPage = (props) => {
  const cartContext = useCartContext();
  const editorContext = useEditorContext();
  const dataContext = useDataContext();

  const product = dataContext.getProduct(props.productId);
  const defaultPrintOnly = product.config.defaultPrintOnly ?? true;

  /**
   * Configuration { product, primaryProductData, secondaryProductData, price, bundle }
   */
  const [configuration, setConfiguration] = useState(() =>
    getInitialPriceData(product, dataContext, defaultPrintOnly),
  );
  // The picker's initial SKU (config.defaultSku or the first by position), so bundles match it
  const [skuProduct, setSkuProduct] = useState(configuration.product);

  // Reset during render: a reset effect would run after the picker's mount onChange and wipe it
  const [shownProduct, setShownProduct] = useState(product);
  if (shownProduct !== product) {
    const initial = getInitialPriceData(product, dataContext, defaultPrintOnly);
    setShownProduct(product);
    setSkuProduct(initial.product);
    setConfiguration(initial);
  }

  const mightLikeProducts = dataContext.getProducts(product.related);

  useEffect(() => {
    tracker.productView(skuProduct);
  }, [skuProduct]);

  const handleOnAddToCartClick = () => {
    cartContext.addItem(
      configuration.product,
      configuration.product.version,
      configuration.primaryProductData,
      false,
      prepareBundleData(dataContext, configuration),
    );
  };

  const handleOnStartEditorClick = () => {
    if (configuration.bundle || configuration.upsell) {
      editorContext.createItem(
        configuration.product.id,
        configuration.product.version,
        prepareBundleData(dataContext, configuration),
      );
    } else {
      editorContext.removeBundle(configuration.product.id);
    }
  };

  const handleOnChangeConfiguration = (data) => {
    if (data.product) {
      setSkuProduct(data.product);
    }
    setConfiguration(data);
  };

  const markupImages = productStillImages(product).map((image) =>
    getCloudinaryUrl({ imageKey: image.key, prefix: 'products/', type: 'big', format: 'jpg' }),
  );

  return (
    <React.Fragment key={product.id}>
      <Head
        title={props.content.seoTitle ?? props.content.caption}
        fullTitle={props.content.seoFullTitle}
        canonicalPath={props.content.canonicalPath}
        keywords={props.content.seoKeywords}
        description={props.content.seoDescription ?? props.content.shortDescription}
        image={props.content.seoUrl}
      />

      <ScriptMarkupProduct
        id={product.id}
        url={`${SETTINGS.HOST}${product.url}`}
        name={props.content.caption}
        description={props.content.shortDescription}
        images={markupImages}
        prices={product.prices}
        currency={product.currency}
        brand="MyCanvas"
        host={SETTINGS.HOST}
      />

      {props.content.faq && <ScriptMarkupFaq items={faqContentToItems(props.content.faq)} />}

      <Layout>
        <Section className="section-product">
          <Container>
            <Grid.Row className="row-product-detail">
              {/* Buy box column is DOM-first so the h1 precedes the FAQ h2/h3 for
                  crawlers; order-lg-2 keeps it visually on the right at lg+ */}
              <Grid.Col xs={12} lg={5} className="pl-lg-2 col-product-detail order-lg-2">
                <div className="product-detail-cell col-product-data order-2">
                  <Typography.H1 className="mb-3">{props.content.caption}</Typography.H1>

                  <div className="list-styled mb-4">
                    <HtmlContent html={props.content.description} />
                  </div>

                  {skuProduct ? (
                    <>
                      <div>
                        <ProductConfiguration
                          product={product}
                          skuProduct={skuProduct}
                          onChange={handleOnChangeConfiguration}
                          cardClassName="card-mycanvas"
                          priceLabelClassName="text-body"
                          hidden={product.config.hideProductConfiguration}
                          defaultPrintOnly={defaultPrintOnly}
                          hidePrintOnly={product.config.hidePrintOnly ?? false}
                          showSkuDescription={product.config.showSkuDescription}
                          labels={product.config.labels}
                        />
                        {/* Price label and value are UI text, not content headings — divs keep the heading styling */}
                        <div className="h6">
                          PRICE{product.config.showSkuPriceStartingAt && ' STARTING AT'}
                        </div>
                        <div className="h3 mb-3">
                          {formatPrice(configuration.price, product.currency, true)}
                        </div>
                      </div>
                      {skuProduct.display ? (
                        <CartPageLink>
                          <Button type="primary" block onClick={handleOnAddToCartClick}>
                            Add to Cart
                          </Button>
                        </CartPageLink>
                      ) : (
                        <>
                          {product.config.continueButtonUrl ? (
                            <Link
                              href={queryString.addParams(product.config.continueButtonUrl, {
                                product: product.id,
                                ...configuration.config,
                              })}
                              passHref
                              legacyBehavior
                            >
                              <Button type="primary" block onClick={handleOnStartEditorClick}>
                                Continue
                              </Button>
                            </Link>
                          ) : (
                            <EditorPageLink
                              productId={skuProduct.id}
                              version={skuProduct.version}
                              quantity={configuration.quantity}
                              source={product.config.source}
                            >
                              <Button type="primary" block onClick={handleOnStartEditorClick}>
                                Continue
                              </Button>
                            </EditorPageLink>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <Alert type="danger" className="text-center">
                      Sorry, this product is not available.
                    </Alert>
                  )}
                </div>

                {props.content.testimonial && (
                  <div className="product-detail-cell order-4">
                    <CustomerReview className="mt-2 customer-review-mycanvas">
                      {/* Quote is UI text, not a content heading — div keeps the h3 styling */}
                      <div className="h3 customer-review-text">
                        {props.content.testimonial.quote}
                      </div>
                      <CustomerReview.Customer>
                        <Typography.Span>{props.content.testimonial.author}</Typography.Span>
                      </CustomerReview.Customer>
                    </CustomerReview>
                  </div>
                )}
              </Grid.Col>

              <Grid.Col xs={12} lg={7} className="pr-lg-2 col-product-detail order-lg-1">
                <div className="product-detail-cell order-1">
                  <ProductDetailCarousel images={product.images} />
                </div>

                {(props.content.spec.length > 0 || props.content.faq) && (
                  <div className="product-detail-cell order-3 mt-2">
                    {props.content.spec.length > 0 && (
                      <Card className="mb-1 card-mycanvas card-mycanvas-nohover">
                        <Card.Body className="text-body">
                          <HtmlContent html={props.content.spec} />
                        </Card.Body>
                      </Card>
                    )}
                    {props.content.faq && (
                      <Accordion title="FAQ" className="accordion-mycanvas">
                        {props.content.faq.map((faq) => (
                          <Accordion.Item key={faq.caption} title={faq.caption}>
                            <HtmlContent html={faq.description} />
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </div>
                )}
              </Grid.Col>
            </Grid.Row>
          </Container>
        </Section>

        {skuProduct?.id === 'pexeso' && (
          <Section>
            <Container className="my-5 text-center">
              <Typography.H2>HOW TO PLAY?</Typography.H2>
              <Typography.P className="mt-1 mb-3">
                In the memory game, players take turns flipping two cards and trying to find a
                matching pair.
                <br />
                If they succeed, they keep the cards and continue; if not, the cards are turned back
                over and the next player takes a turn.
              </Typography.P>

              <PexesoGamePreview
                assets={[
                  [skuProduct.templateRootPath, 'assets_client/images/female-avatar.png?_'].join(
                    '/',
                  ),
                  [skuProduct.templateRootPath, 'assets_client/images/male-avatar.png?_'].join('/'),
                  [skuProduct.templateRootPath, 'assets_client/images/male-avatar-01.png?_'].join(
                    '/',
                  ),
                ]}
                perRow={6}
                primaryColor={EditorTheme.colors.primary}
              />
            </Container>
          </Section>
        )}

        {mightLikeProducts.length > 0 && (
          <Section>
            <Container>
              {/* h2 section heading; .h3 keeps the original visual size */}
              <Typography.H2 className="h3 section-title">YOU MIGHT LIKE</Typography.H2>
              <ProductListCarousel
                products={mightLikeProducts}
                className="mb-2"
                renderItem={(mightLikeProduct, index) => (
                  <ProductDetailLink productId={mightLikeProduct.id}>
                    <ProductCard
                      asLink
                      product={mightLikeProduct}
                      className={`card-mycanvas card-color-variant-${index}`}
                    />
                  </ProductDetailLink>
                )}
              />
            </Container>
          </Section>
        )}

        {props.content.extraDescription && (
          <Section>
            <Container className="my-5">
              <HtmlContent html={props.content.extraDescription} />
            </Container>
          </Section>
        )}
      </Layout>
    </React.Fragment>
  );
};

LegacyProductDetailPage.propTypes = {
  productId: PropTypes.string.isRequired,
  content: PropTypes.object,
  breadcrumb: PropTypes.array,
};

// Content format 2 switches the whole page to the redesigned layout. The key remounts the
// page per product, so client-side navigation starts from the new product's state.
const ProductDetailPage = (props) =>
  usesProductPageLayout(props.content) ? (
    <ProductPage key={props.productId} {...props} />
  ) : (
    <LegacyProductDetailPage {...props} />
  );

ProductDetailPage.getInitialProps = async () => {
  throw new Error('Cannot access this page directly. See /[...params] page');
};

ProductDetailPage.propTypes = {
  productId: PropTypes.string.isRequired,
  content: PropTypes.object,
  breadcrumb: PropTypes.array,
};

export default ProductDetailPage;
